import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, Eye } from "lucide-react";
import { api, type RealtimeResponse } from "../lib/api";
import { minuteLabel, nf } from "../lib/format";
import { BarList } from "../components/BarList";
import { ErrorBox, PageSpinner } from "../components/ui";
import type { SiteOutletContext } from "./SiteLayout";

export default function RealtimePage() {
  const { site } = useOutletContext<SiteOutletContext>();

  const realtime = useQuery({
    queryKey: ["realtime", site.id],
    queryFn: () => api.get<RealtimeResponse>(`/api/sites/${site.id}/realtime`),
    refetchInterval: 15000,
  });

  if (realtime.isLoading) return <PageSpinner />;
  if (realtime.error || !realtime.data) return <ErrorBox message="Gagal memuat data realtime." />;

  const data = realtime.data;
  const chartData = data.series.map((point) => ({ ...point, label: minuteLabel(point.t, site.tz) }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <div className="card p-3.5">
          <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Activity size={14} className="text-emerald-600" />
            Pengunjung (30 menit)
          </dt>
          <dd className="mt-1.5 text-2xl font-semibold tabular-nums text-slate-900">{nf.format(data.visitors)}</dd>
        </div>
        <div className="card p-3.5">
          <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Eye size={14} className="text-blue-600" />
            Pageview (30 menit)
          </dt>
          <dd className="mt-1.5 text-2xl font-semibold tabular-nums text-slate-900">{nf.format(data.pageviews)}</dd>
        </div>
      </div>

      <section className="card p-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Aktivitas per menit</h3>
        {chartData.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">Belum ada aktivitas 30 menit terakhir.</p>
        ) : (
          <div className="h-52 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={28}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickLine={false}
                  axisLine={false}
                  width={32}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value, name) => [nf.format(Number(value)), name === "visitors" ? "Pengunjung" : "Pageview"]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Bar dataKey="pageviews" name="pageviews" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <BarList
          title="Halaman aktif"
          items={data.pages.map((row) => ({ key: row.val, label: row.val, value: row.visitors, extra: row.pageviews }))}
          extraLabel="Pageview"
        />
        <BarList
          title="Sumber"
          items={data.referrers.map((row) => ({
            key: row.val,
            label: row.val === "" ? "Langsung" : row.val,
            value: row.visitors,
            extra: row.pageviews,
          }))}
          extraLabel="Pageview"
        />
        <BarList
          title="Negara"
          items={data.countries.map((row) => ({ key: row.val, label: row.val, value: row.visitors, extra: row.pageviews }))}
          extraLabel="Pageview"
        />
        <BarList
          title="Peristiwa"
          items={data.events.map((row) => ({ key: row.val, label: row.val, value: row.visitors, extra: row.pageviews }))}
          extraLabel="Jumlah"
        />
      </div>
    </div>
  );
}
